// Notifications inbox (GDD 08 screen 8, 04 §5.17). Five tabs with unread counts in `?tab=` (G-80);
// without one it opens the first tab with unread notices. Tapping a notice marks it read and opens
// its target (spec §3.7); Mark all read and Delete read work per tab, each notice can be deleted
// (G-81). The gear opens the inbox settings: which notice types arrive at all (G-82).
import {
  type Notification as InboxNotification,
  NOTIFICATION_CATEGORIES,
  type NotificationCategory,
  type NotificationCountsByCategory,
} from '@stars-and-ships/api-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Settings, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useOutletContext, useSearchParams } from 'react-router';
import { endpoints } from '../api/endpoints';
import { queryKeys, useNotifications } from '../api/queries';
import type { GameContext } from '../components/GameLayout';
import { InboxSettingsSheet } from '../components/InboxSettingsSheet';
import {
  Button,
  ErrorNotice,
  IconButton,
  Notice,
  Page,
  Sheet,
  Spinner,
  type TabItem,
  TabPanel,
  Tabs,
} from '../components/ui';
import {
  FACILITY_LABELS,
  formatNumber,
  SHIP_LABELS,
  NOTIFICATION_TAB_LABELS as TAB_LABELS,
} from '../labels';
import { notificationTarget } from '../notificationTargets';

const text = (value: unknown) => (typeof value === 'string' ? value : '');
const num = (value: unknown) => (typeof value === 'number' ? value : 0);

export function notificationText(n: InboxNotification): string {
  const p = n.payload;
  switch (n.type) {
    case 'BUILDING_COMPLETED': {
      const label = FACILITY_LABELS[p.facilityType as keyof typeof FACILITY_LABELS] ?? 'A building';
      const where = text(p.systemName) ? ` in ${text(p.systemName)}` : '';
      return `${label} reached level ${num(p.level)}${where}.`;
    }
    case 'PORT_TRADE_COMPLETED':
      return `Trade complete: +${formatNumber(num(p.creditsOut))} Credits (balance ${formatNumber(num(p.credits))}).`;
    case 'SHIELD_WARNING':
      return `Your beginner shield ends ${new Date(text(p.shieldUntil)).toLocaleString()}.`;
    case 'SHIELD_EXPIRED':
      return 'Your beginner shield has ended.';
    case 'SHIP_PRODUCED': {
      const ship = SHIP_LABELS[p.shipType as keyof typeof SHIP_LABELS] ?? 'Ship';
      const where = text(p.systemName) ? ` in ${text(p.systemName)}` : '';
      return `${formatNumber(num(p.quantity))} × ${ship} built${where}.`;
    }
    case 'FLEET_INCOMING': {
      const attacker = (p.attacker as { displayName?: unknown } | undefined)?.displayName;
      const when = text(p.arrivesAt) ? new Date(text(p.arrivesAt)).toLocaleString() : 'soon';
      return `Hostile fleet of ${text(attacker) || 'an enemy'} arriving at ${text(p.systemName) || 'your system'} ${when}.`;
    }
    case 'FLEET_INCOMING_CANCELLED':
      return 'An incoming hostile fleet turned back.';
    case 'FLEET_ORDER_FAILED':
      return `Your timed ${text(p.mission) || 'fleet'} on ${text(p.systemName) || 'a system'} did not leave: ${text(p.message)}`;
    case 'FLEET_ORDER_RESCHEDULED': {
      const where = text(p.systemName) || 'a system';
      const leaves = new Date(text(p.departAt)).toLocaleString();
      const arrives = new Date(text(p.arriveAt)).toLocaleString();
      return text(p.arriveAt) === text(p.plannedArriveAt)
        ? `The gate network changed: your fleet to ${where} now leaves ${leaves} and still arrives ${arrives}.`
        : `The gate network changed: your fleet to ${where} can no longer arrive on time. It leaves ${leaves} and arrives ${arrives}.`;
    }
    case 'BATTLE_STARTED':
      return `Battle at ${text(p.systemName) || 'a system'} — you are the ${text(p.role) || 'defender'}.`;
    case 'BATTLE_ENDED': {
      const won = (text(p.role) === 'attacker') === (text(p.outcome) === 'attacker_won');
      const captured = p.systemCaptured === true && text(p.role) === 'attacker';
      return `${won ? 'Victory' : 'Defeat'} at ${text(p.systemName) || 'a system'}${captured ? ' — system captured' : ''}.`;
    }
    case 'SCOUT_RETURNED':
      return p.succeeded === true
        ? `Scout report ready for ${text(p.systemName) || 'a system'}.`
        : `Your scout failed at ${text(p.systemName) || 'a system'}: it carried too little Influence.`;
    case 'SCOUT_FAILED_AGAINST_YOU':
      return `A scout failed against ${text(p.systemName) || 'your system'} — ${formatNumber(num(p.influenceLost))} Influence lost.`;
    case 'CLAIM_PLACED': {
      const claimant = (p.claimant as { displayName?: unknown } | undefined)?.displayName;
      const when = text(p.activatesAt) ? new Date(text(p.activatesAt)).toLocaleString() : 'soon';
      return `${text(claimant) || 'A commander'} claimed ${text(p.systemName) || 'your system'}. The claim activates ${when}.`;
    }
    case 'CLAIM_ACTIVATED':
      return text(p.role) === 'claimant'
        ? `Your claim on ${text(p.systemName) || 'a system'} is active — win a battle there to capture it.`
        : `The claim on ${text(p.systemName) || 'your system'} is now active.`;
    case 'CLAIM_RESOLVED': {
      const where = text(p.systemName) || 'a system';
      switch (text(p.status)) {
        case 'fulfilled':
          return `You captured ${where}.`;
        case 'invalidated':
          return `Your claim on ${where} ended because its owner changed — ${formatNumber(num(p.refundedCredits))} Credits refunded.`;
        case 'expired':
          return `Your claim on ${where} expired.`;
        default:
          return `Your claim on ${where} was ${text(p.status)}.`;
      }
    }
    case 'SYSTEM_OWNER_CHANGED':
      return `You lost ${text(p.systemName) || 'a system'}.`;
    case 'ALLIANCE_INVITE_RECEIVED':
      return `${allianceName(p)} invited you to join. Open it to accept or decline.`;
    case 'ALLIANCE_APPLICATION_RECEIVED':
      return `${text(p.applicantName) || 'A commander'} applied to join your alliance.`;
    case 'ALLIANCE_APPLICATION_ACCEPTED':
      return `Your application was accepted — welcome to ${allianceName(p)}.`;
    case 'ALLIANCE_RANK_CHANGED':
      return `Your alliance rank is now ${RANK_NAMES[text(p.rank)] ?? text(p.rank)}.`;
    case 'ALLIANCE_KICKED':
      return `You were removed from ${allianceName(p)}. You can rejoin it after ${
        text(p.rejoinAllowedAt) ? new Date(text(p.rejoinAllowedAt)).toLocaleString() : '48 hours'
      }.`;
    case 'ALLIANCE_DISBANDED':
      return `${allianceName(p)} was disbanded.`;
    default:
      return humanizeType(n.type);
  }
}

const RANK_NAMES: Record<string, string> = {
  overlord: 'Overlord',
  warlord: 'Warlord',
  commander: 'Commander',
  recruit: 'Recruit',
};

const allianceName = (p: Record<string, unknown>) =>
  text(p.allianceName)
    ? `${text(p.allianceName)}${text(p.allianceTag) ? ` [${text(p.allianceTag)}]` : ''}`
    : 'An alliance';

/** A notification type without its own text never shows as a code: `SOME_EVENT` → "Some event". */
export const humanizeType = (type: string): string => {
  const words = type.toLowerCase().replaceAll('_', ' ');
  return `${words.charAt(0).toUpperCase()}${words.slice(1)}.`;
};

const parseTab = (value: string | null): NotificationCategory | null =>
  NOTIFICATION_CATEGORIES.find((c) => c === value) ?? null;

export function InboxScreen() {
  const { galaxyId } = useOutletContext<GameContext>();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const requested = parseTab(params.get('tab'));
  // Without `?tab=`, the first answer's counts pick the first tab with unread notices.
  const [fallback, setFallback] = useState<NotificationCategory | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const tab = requested ?? fallback ?? 'combat';
  const inbox = useNotifications(galaxyId, tab);
  const lastCounts = useRef<NotificationCountsByCategory | undefined>(undefined);
  if (inbox.data) lastCounts.current = inbox.data.unreadByCategory;
  const counts = lastCounts.current;
  useEffect(() => {
    if (requested || fallback || !counts) return;
    setFallback(NOTIFICATION_CATEGORIES.find((c) => counts[c] > 0) ?? 'combat');
  }, [requested, fallback, counts]);
  // Keep the bell's "opened from" state, so closing the inbox still returns there.
  const setTab = (next: NotificationCategory) =>
    setParams({ tab: next }, { replace: true, state: location.state });

  const tabs: TabItem<NotificationCategory>[] = NOTIFICATION_CATEGORIES.map((id) => {
    const unread = counts?.[id] ?? 0;
    return {
      id,
      label: TAB_LABELS[id],
      badge:
        unread > 0 ? (
          <span
            data-unread-badge={id}
            className="bg-accent px-1 font-mono text-[10px] text-accent-ink"
          >
            {unread}
          </span>
        ) : undefined,
    };
  });

  return (
    <Page>
      <div className="flex items-center justify-between gap-2">
        <h1 className="font-display text-xl font-bold uppercase tracking-[0.08em] text-text">
          Inbox
        </h1>
        <IconButton label="Inbox settings" onClick={() => setSettingsOpen(true)}>
          <Settings className="size-5" aria-hidden />
        </IconButton>
      </div>
      <InboxSettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <div className="mt-3">
        <Tabs label="Inbox" tabs={tabs} value={tab} onChange={setTab} />
        <TabPanel id={tab}>
          {requested || fallback ? (
            <InboxTab
              galaxyId={galaxyId}
              category={tab}
              items={inbox.data?.items}
              unread={counts?.[tab] ?? 0}
              pending={inbox.isPending}
              error={inbox.error}
            />
          ) : (
            <Spinner />
          )}
        </TabPanel>
      </div>
    </Page>
  );
}

function InboxTab({
  galaxyId,
  category,
  items,
  unread,
  pending,
  error,
}: {
  galaxyId: string;
  category: NotificationCategory;
  items: InboxNotification[] | undefined;
  unread: number;
  pending: boolean;
  error: Error | null;
}) {
  const { me } = useOutletContext<GameContext>();
  const home = me?.systems[0]?.id;
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.notifications(galaxyId) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.galaxyMe(galaxyId) });
  };
  const markRead = useMutation({
    mutationFn: (ids: string[] | 'all') =>
      endpoints.markNotificationsRead(galaxyId, ids === 'all' ? { all: true, category } : { ids }),
    onSettled: refresh,
  });
  const remove = useMutation({
    mutationFn: (ids: string[] | 'read') =>
      endpoints.deleteNotifications(galaxyId, ids === 'read' ? { read: true, category } : { ids }),
    onSuccess: () => setConfirmDelete(false),
    onSettled: refresh,
  });
  const hasRead = items?.some((n) => n.readAt) ?? false;
  const open = (n: InboxNotification) => {
    if (!n.readAt) markRead.mutate([n.id]);
  };

  return (
    <div className="space-y-3">
      {unread > 0 || hasRead ? (
        <div className="flex flex-wrap justify-end gap-2">
          {unread > 0 ? (
            <Button
              variant="ghost"
              disabled={markRead.isPending}
              onClick={() => markRead.mutate('all')}
            >
              Mark all read
            </Button>
          ) : null}
          {hasRead ? (
            <Button variant="ghost" onClick={() => setConfirmDelete(true)}>
              Delete read
            </Button>
          ) : null}
        </div>
      ) : null}
      {pending ? <Spinner /> : null}
      {error ? <ErrorNotice error={error} /> : null}
      {markRead.error ? <ErrorNotice error={markRead.error} /> : null}
      {remove.error && !confirmDelete ? <ErrorNotice error={remove.error} /> : null}
      {items?.length === 0 ? <Notice>No {TAB_LABELS[category]} notices.</Notice> : null}
      <ul className="space-y-2">
        {items?.map((n) => {
          const target = notificationTarget(n.type, n.payload, home);
          const body = (
            <>
              <p>{notificationText(n)}</p>
              <p className="mt-1 text-xs text-faint">{new Date(n.createdAt).toLocaleString()}</p>
            </>
          );
          return (
            <li
              key={n.id}
              data-notification={n.type}
              data-unread={n.readAt ? undefined : ''}
              className={`hud-cut-sm flex items-stretch border text-sm ${
                n.readAt ? 'border-line bg-panel text-muted' : 'border-info/50 bg-info/10 text-text'
              }`}
            >
              {target ? (
                <Link
                  to={`/g/${galaxyId}/${target}`}
                  onClick={() => open(n)}
                  className="flex min-w-0 flex-1 items-center gap-3 p-3 hover:bg-panel-raised"
                >
                  <div className="min-w-0 flex-1">{body}</div>
                  <ChevronRight className="size-4 shrink-0 text-faint" aria-hidden />
                </Link>
              ) : n.readAt ? (
                <div className="min-w-0 flex-1 p-3">{body}</div>
              ) : (
                <button
                  type="button"
                  onClick={() => open(n)}
                  className="min-w-0 flex-1 p-3 text-left hover:bg-panel-raised"
                >
                  {body}
                </button>
              )}
              <IconButton
                label="Delete notice"
                disabled={remove.isPending}
                onClick={() => remove.mutate([n.id])}
                className="h-auto shrink-0"
              >
                <Trash2 className="size-4" aria-hidden />
              </IconButton>
            </li>
          );
        })}
      </ul>
      <Sheet
        open={confirmDelete}
        title={`Delete read ${TAB_LABELS[category]} notices?`}
        onClose={() => setConfirmDelete(false)}
      >
        <div className="space-y-3">
          <p className="text-sm text-muted">Unread notices stay. This cannot be undone.</p>
          {remove.error ? <ErrorNotice error={remove.error} /> : null}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => remove.mutate('read')}
            >
              Delete read
            </Button>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
